import React, { useEffect, useState } from 'react';
import { useWallet } from '../context/WalletContext';
import { useContracts } from '../hooks/useContracts';
import { useUserData } from '../hooks/useUserData';
import { usePlanConfig } from '../hooks/usePlanConfig';
import { fmtUSD, bpsToPct } from '../utils/format';
import { rankLabel, rankTag, displayRank } from '../utils/constants';
import GlassCard from '../components/common/GlassCard';
import StatTile from '../components/common/StatTile';
import ProgressBar from '../components/common/ProgressBar';
import ConnectPrompt from '../components/common/ConnectPrompt';

const Ranks = () => {
  const { account } = useWallet();
  const c = useContracts();
  const { userId, summary } = useUserData();
  const { ranks } = usePlanConfig();

  const [progress, setProgress] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!userId || userId === 0n) return;
      try {
        const p = await c.read.views.getRankProgress(userId);
        if (mounted) setProgress(p);
      } catch {}
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (!account) return <ConnectPrompt />;

  const currentRank = Number(summary?.rank || 0);

  return (
    <div className="animate-fade-up">
      <div className="page-title">Rank Progression</div>
      <div className="page-subtitle">Grow legs to unlock higher rank income tiers.</div>

      <div className="grid grid-3 mb-4">
        <StatTile
          label="Current Rank"
          value={rankTag(currentRank)}
          sub={rankLabel(currentRank)}
          color="neon"
        />
        <StatTile
          label="Main Leg"
          value={fmtUSD(progress?.mainLeg || 0n)}
          color="cyan"
        />
        <StatTile
          label="Other Legs"
          value={fmtUSD(progress?.otherLegs || 0n)}
        />
      </div>

      {progress && Number(progress.currentRank) < 9 && (
        <GlassCard title={`Next Rank: ${rankTag(progress.nextRank)} · ${rankLabel(progress.nextRank)}`}>
          <div className="mb-2">
            <div className="input-label">
              <span>Main Leg</span>
              <span>{fmtUSD(progress.mainLeg)} / {fmtUSD(progress.nextMainLegRequired)}</span>
            </div>
            <ProgressBar value={Number(progress.mainLeg)} max={Number(progress.nextMainLegRequired) || 1} />
            {BigInt(progress.mainLeg) < BigInt(progress.nextMainLegRequired) && (
              <div className="mono mt-1" style={{ fontSize: 12, color: 'var(--grx-warn)' }}>
                Need {fmtUSD(BigInt(progress.nextMainLegRequired) - BigInt(progress.mainLeg))} more.
              </div>
            )}
          </div>
          <div className="mt-3">
            <div className="input-label">
              <span>Other Legs</span>
              <span>{fmtUSD(progress.otherLegs)} / {fmtUSD(progress.nextOtherLegsRequired)}</span>
            </div>
            <ProgressBar value={Number(progress.otherLegs)} max={Number(progress.nextOtherLegsRequired) || 1} />
            {BigInt(progress.otherLegs) < BigInt(progress.nextOtherLegsRequired) && (
              <div className="mono mt-1" style={{ fontSize: 12, color: 'var(--grx-warn)' }}>
                Need {fmtUSD(BigInt(progress.nextOtherLegsRequired) - BigInt(progress.otherLegs))} more.
              </div>
            )}
          </div>
          <div className="mt-3">
            <div className="input-label">
              <span>Self Investment</span>
              <span>{fmtUSD(progress.selfInvestUSDT)} / {fmtUSD(progress.nextSelfRequiredUSDT)}</span>
            </div>
            <ProgressBar value={Number(progress.selfInvestUSDT)} max={Number(progress.nextSelfRequiredUSDT) || 1} />
            {BigInt(progress.selfInvestUSDT) < BigInt(progress.nextSelfRequiredUSDT) && (
              <div className="mono mt-1" style={{ fontSize: 12, color: 'var(--grx-warn)' }}>
                Need {fmtUSD(BigInt(progress.nextSelfRequiredUSDT) - BigInt(progress.selfInvestUSDT))} more self-investment.
              </div>
            )}
          </div>
        </GlassCard>
      )}

      <div className="mt-3" />

      <GlassCard title="All Ranks">
        {ranks && ranks.map((r, i) => {
          const isCurrent = Number(r.rank) === currentRank;
          const achieved = Number(r.rank) <= currentRank;
          return (
            <div key={i} className={`rank-item ${isCurrent ? 'current' : ''} ${achieved && !isCurrent ? 'achieved' : ''}`}>
              <div className="rank-badge">R{displayRank(r.rank)}</div>
              <div style={{ flex: 1 }}>
                <div className="display" style={{ fontSize: 14, color: 'var(--grx-text)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {rankLabel(r.rank)}
                </div>
                <div className="mono" style={{ fontSize: 12, color: 'var(--grx-text-dim)', marginTop: 4 }}>
                  Main: {fmtUSD(r.mainLegRequired)} · Others: {fmtUSD(r.otherLegsRequired)} · Self: {fmtUSD(r.selfRequiredUSDT)}
                </div>
              </div>
              <div className="display" style={{ color: 'var(--grx-neon)', fontWeight: 800 }}>
                {bpsToPct(r.percentageBps)}
              </div>
            </div>
          );
        })}
      </GlassCard>
    </div>
  );
};

export default Ranks;
